import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Play, Smartphone, Apple, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { heroData } from '../data/mock';

const HeroSection = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  
  const dashboardImages = [
    {
      url: heroData.dashboardUrl,
      title: "XAI Dashboard Interface",
      description: "Antarmuka dashboard lengkap"
    },
    {
      url: heroData.dashboardUrl2,
      title: "Interaktif Gridview", 
      description: "Pengalaman desktop yang elegan"
    }
  ];

  useEffect(() => {
    // Trigger content animation after component mounts (slower)
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Auto-rotate images every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % dashboardImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [dashboardImages.length]);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % dashboardImages.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + dashboardImages.length) % dashboardImages.length);
  };

  return (
    <section id="hero" className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black pt-16 relative overflow-hidden">
      {/* Glossy Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-transparent to-purple-600/5"></div>
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center min-h-screen py-12">
          {/* Left Content */}
          <div className={`lg:col-span-2 space-y-6 lg:space-y-8 lg:pr-4 transition-all duration-2000 ease-out transform ${
            showContent ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
          }`}>
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-blue-900/30 text-blue-300 rounded-full text-sm font-medium border border-blue-500/20">
                🚀 Software ERP & Akuntansi Terdepan
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  {heroData.title}
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed max-w-2xl">
                {heroData.description}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 relative z-10">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                onClick={() => window.open('https://api.whatsapp.com/send/?phone=6281218691110&text=Halo%20BTSSOFT%2C%20saya%20ingin%20konsultasi%20gratis%20mengenai%20XAI%20ERP%20System.%20Mohon%20informasi%20lebih%20lanjut.&type=phone_number&app_absent=0', '_blank')}
              >
                {heroData.primaryCta}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              
              <Link to="/demo">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:border-blue-400 hover:text-blue-400"
                >
                  <Play className="mr-2 h-5 w-5" />
                  {heroData.secondaryCta}
                </Button>
              </Link>
            </div>

            {/* Mobile App Downloads */}
            <div className="pt-6 border-t border-gray-700">
              <p className="text-sm text-gray-400 mb-4">Download Mobile App:</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  className="border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white px-6 py-3 rounded-lg transition-all duration-300"
                  onClick={() => window.open('https://play.google.com/store/apps/details?id=com.xaiapi.xaiapp&hl=en-US', '_blank')}
                >
                  <Smartphone className="mr-2 h-4 w-4 text-green-400" />
                  Download for Android
                </Button>
                <Button
                  variant="outline" 
                  className="border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white px-6 py-3 rounded-lg transition-all duration-300"
                  onClick={() => window.open('https://apps.apple.com/id/app/xai-cloud-btssoft/id6470180081', '_blank')}
                >
                  <Apple className="mr-2 h-4 w-4 text-gray-400" />
                  Download for iOS
                </Button>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="pt-8 border-t border-gray-700">
              <p className="text-sm text-gray-400 mb-4">Dipercaya oleh 3000+ UMKM di Indonesia</p>
              <div className="flex items-center space-x-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">3000+</div>
                  <div className="text-sm text-gray-400">Klien Aktif</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">24/7</div>
                  <div className="text-sm text-gray-400">Support</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Dashboard Preview Carousel */}
          <div className={`lg:col-span-3 relative transition-all duration-2500 ease-out transform ${
            showContent ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-30 opacity-0 scale-90'
          }`}>
            <div className="relative bg-black/40 backdrop-blur-xl rounded-2xl shadow-2xl p-6 transform rotate-1 hover:rotate-0 transition-all duration-500 border border-white/10 before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/5 before:to-transparent before:rounded-2xl hover:shadow-blue-500/20 overflow-hidden">
              
              {/* Image Carousel */}
              <div className="relative">
                {/* Loading overlay */}
                {!imageLoaded && (
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
                    <div className="flex items-center space-x-3 text-white">
                      <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-lg">Loading Dashboard...</span>
                    </div>
                  </div>
                )}

                {/* Current Image */}
                <img 
                  src={dashboardImages[currentImage].url}
                  alt={dashboardImages[currentImage].title}
                  className={`w-full h-auto max-h-[500px] object-cover rounded-xl transition-all duration-1500 ease-out transform ${
                    imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-110'
                  }`}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageLoaded(true)}
                />

                {/* Navigation Arrows */}
                <button
                  onClick={prevImage}
                  className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-black/60 backdrop-blur-sm text-white p-3 rounded-full hover:bg-black/80 transition-all duration-300 z-20 hover:scale-110"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-black/60 backdrop-blur-sm text-white p-3 rounded-full hover:bg-black/80 transition-all duration-300 z-20 hover:scale-110"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>

                {/* Dots Indicator */}
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
                  {dashboardImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImage(index)}
                      className={`w-4 h-4 rounded-full transition-all duration-300 ${
                        index === currentImage ? 'bg-blue-400 scale-125' : 'bg-white/50 hover:bg-white/70'
                      }`}
                    />
                  ))}
                </div>

                {/* Image Title */}
                <div className="absolute bottom-16 left-6 right-6 text-center z-20">
                  <div className="bg-black/70 backdrop-blur-sm rounded-xl px-6 py-3">
                    <h4 className="text-white font-bold text-lg">{dashboardImages[currentImage].title}</h4>
                    <p className="text-gray-300 text-sm">{dashboardImages[currentImage].description}</p>
                  </div>
                </div>

                <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/10 to-transparent rounded-xl"></div>
              </div>
            </div>
            
            {/* Floating Elements with Delayed Animation */}
            <div className={`absolute -top-6 -left-6 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-medium transition-all duration-1800 ease-out transform ${
              imageLoaded ? 'translate-y-0 opacity-100 animate-bounce' : '-translate-y-20 opacity-0'
            }`}>
              ✅ Real-time Data
            </div>
            <div className={`absolute -bottom-6 -right-6 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-medium transition-all duration-2200 ease-out transform ${
              imageLoaded ? 'translate-y-0 opacity-100 animate-pulse' : 'translate-y-20 opacity-0'
            }`}>
              📱 Multi-Platform
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;